import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getWithRetry } from "../lib/apiResilient";
import { motion } from "framer-motion";
import { Check, BookOpen, ArrowUpRight, Heart, X, Sparkles, Loader2 } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function StudioSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null); // {payment_status, status, ...}
  const [error, setError] = useState(null);
  const attempts = useRef(0);
  const autoFiredRef = useRef(false);
  const navigate = useNavigate();

  // 🚀 TRUST-STRIPE FAST-LANE
  // ────────────────────────
  // The moment we land here, Stripe has already redirected the buyer back —
  // which only happens AFTER the card is charged. So instead of making them
  // watch a 2-minute polling loader we call `instant-grant`, which:
  //   • eagerly inserts the studio_orders row (so the library shows it now)
  //   • signs them in (cookie pair)
  //   • returns a deep-link straight to the reader
  // The polling logic below still runs as a safety net for the rare cases
  // where the eager grant fails (e.g. session_id mismatch).
  useEffect(() => {
    if (!sessionId || autoFiredRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/api/studio/checkout/instant-grant/${sessionId}`,
          {},
          { withCredentials: true, timeout: 8000 }
        );
        if (cancelled || autoFiredRef.current) return;
        if (data?.ok && data?.redirect_to) {
          autoFiredRef.current = true;
          // For single-edition buyers we keep them on this page so the upsell
          // can render — but we still pre-grant access in the background.
          if (data.kind === "studio_bundle5" || data.kind === "coaching_call") {
            toast.success("Payment received — opening your shelf.");
            navigate(data.redirect_to, { replace: true });
          }
          // For single editions: mark status as paid optimistically so the
          // upsell + "Open my edition" CTA appear right away. The poll below
          // will reconcile with Stripe's real status as a safety net.
          setStatus((prev) => ({
            ...(prev || {}),
            payment_status: "paid",
            status: "fulfilled",
            kind: data.kind,
            slug: (data.slugs || [])[0],
            instant: true,
          }));
        }
      } catch (e) {
        // Silent — the normal polling loop below will recover.
        // (Stripe still confirms it eventually, just slower without the fast-lane.)
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!sessionId) {
      setError("No session id found.");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await getWithRetry(
          `${API}/api/studio/checkout/status/${sessionId}`,
          { timeout: 6000, withCredentials: true }
        );
        setStatus(data);
        if (data.payment_status === "paid") return;
        if (data.status === "expired" || data.payment_status === "unpaid") return;
        // Aggressive cadence — Stripe usually finalizes within 5-15s when the
        // webhook is configured; this keeps the buyer's perceived wait under 10s.
        //   first 12 tries → 500ms (6s of fast polling)
        //   next 60 tries → 1500ms (90s of moderate polling)
        //   total ceiling ~96s — well before the 130s navigate-anyway timer.
        const delay = attempts.current < 12 ? 500 : 1500;
        if (attempts.current < 72) setTimeout(poll, delay);
      } catch (e) {
        if (e.response?.status === 404) {
          setError("Couldn't find this order. If you completed payment, check your inbox.");
          return;
        }
        if (attempts.current < 72) setTimeout(poll, 1500);
        else setError("We couldn't confirm your payment.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Show an "Open my library" escape hatch after 8s — if a buyer has already
  // closed their wallet and a redirect is still pending, they should never feel
  // trapped. The shelf itself fetches fresh data on mount and will show the
  // order the moment Stripe confirms it.
  const [showLibraryEscape, setShowLibraryEscape] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowLibraryEscape(true), 8000);
    return () => clearTimeout(t);
  }, []);

  // 🚪 Last-resort escape: after 130s with no terminal state, optimistically
  // push the buyer to their library. The webhook will finish fulfillment
  // server-side and the library page fetches fresh data on mount. Aligned with
  // the "up to 2 minutes" promise — gives Stripe the full window plus a buffer.
  // NOTE: skip this once payment has cleared — single-edition buyers stay on
  // the success page to see the bundle upsell.
  useEffect(() => {
    if (!sessionId) return;
    const t = setTimeout(() => {
      if (!autoFiredRef.current && status?.payment_status !== "paid") {
        autoFiredRef.current = true;
        toast.success("Payment confirmed — opening your shelf.");
        navigate("/library", { replace: true });
      }
    }, 130000);
    return () => clearTimeout(t);
  }, [sessionId, navigate, status?.payment_status]);

  // 🎯 Once Stripe confirms payment, push the customer to their content.
  // Single-edition purchases show an upsell instead (no auto-redirect).
  // Bundles & coaching keep the immediate hand-off.
  useEffect(() => {
    if (!status || autoFiredRef.current) return;
    if (status.payment_status !== "paid") return;
    if (status.kind === "coaching_call" || status.no_download) return;
    const isBundleNow = status.kind === "studio_bundle5";
    // Single-edition buyers stay on the page so the bundle upsell can render.
    if (!isBundleNow) return;
    autoFiredRef.current = true;
    toast.success("Welcome — opening your shelf.");
    const t = setTimeout(() => {
      navigate("/library", { replace: true });
    }, 400);
    return () => clearTimeout(t);
  }, [status, navigate]);

  if (error) {
    return (
      <div className="bg-cream min-h-screen pt-40 text-center px-6">
        <p className="text-ink/60 font-serif italic mb-6">{error}</p>
        <Link to="/studio" className="text-xs tracking-[0.25em] uppercase underline">
          Back to the Studio
        </Link>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-cream min-h-screen pt-32 sm:pt-40 px-6" data-testid="success-confirming">
        <OrderInProgress phase="confirming" />
        {showLibraryEscape && (
          <div className="mt-12 border-t border-ink/15 pt-10 max-w-md mx-auto text-center">
            <p className="overline text-ink/60 mb-4">Already paid?</p>
            <p className="text-ink/70 mb-5 text-sm">
              If your card was charged, your edition is already on your shelf — you can open it now. If your card was <em>not</em> charged, you can safely try again from the Studio.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/library" data-testid="success-escape-library" className="btn-ink">Open my library →</Link>
              <Link to="/studio" data-testid="success-escape-studio" className="btn-ghost">Back to the Studio</Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  // If Stripe says unpaid or session expired, surface it explicitly — no endless loader.
  if (status.payment_status === "unpaid" || status.status === "expired") {
    return (
      <div className="bg-cream min-h-screen pt-40 text-center px-6" data-testid="success-unpaid">
        <p className="text-[10px] tracking-[0.3em] uppercase text-ink/55 mb-3">Payment not completed</p>
        <h1 className="font-display uppercase text-4xl md:text-5xl mb-4 max-w-2xl mx-auto leading-tight">
          Your card was <span className="serif-italic font-serif normal-case">never charged</span>.
        </h1>
        <p className="font-serif italic text-ink/70 max-w-md mx-auto mb-10 leading-snug">
          Looks like the checkout closed before payment cleared. Nothing was billed to your account — you can safely try again.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Link to="/studio" className="btn-ink" data-testid="success-unpaid-retry">Back to the Studio →</Link>
          <Link to="/library" className="btn-ghost" data-testid="success-unpaid-library">Open my library</Link>
        </div>
      </div>
    );
  }

  const paid = status.payment_status === "paid";
  const isBundle = status.kind === "studio_bundle5";
  const isCoaching = status.kind === "coaching_call" || status.no_download;
  const downloadUrl = status.download_url ? `${API}${status.download_url}` : null;

  return (
    <div className="bg-cream min-h-screen text-ink">
      <Toaster/>
      <section className="pt-40 pb-24 px-6">
        <div className="max-w-screen-md mx-auto text-center">
          {paid ? (
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <div className="w-20 h-20 mx-auto bg-ink text-cream grid place-items-center rounded-full mb-8">
                <Check className="w-9 h-9" strokeWidth={1.4}/>
              </div>
              <p className="text-[10px] tracking-[0.4em] uppercase text-ink/60 mb-4">
                Order confirmed{!isBundle && status.edition_no ? ` · No. ${status.edition_no}` : ""}
              </p>
              <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mb-6">
                {isBundle ? (
                  <>Your <span className="font-serif italic font-light">Pick-5 bundle</span></>
                ) : (
                  <>Welcome to <span className="font-serif italic font-light">{status.title}</span></>
                )}
              </h1>
              <p className="text-ink/70 mb-12 max-w-lg mx-auto">
                ${Number(status.amount).toLocaleString()} captured · {isBundle
                  ? "Opening your library now — you can read your editions in your browser, anytime."
                  : "Your edition is already on your shelf. A Stripe receipt is on its way to your inbox."}
              </p>

              {/* Single product → Bundle upsell + Library CTA */}
              {!isBundle && !isCoaching && (
                <SingleProductUpsell status={status} />
              )}

              {/* Bundle → take them to the shelf */}
              {isBundle && (
                <Link
                  to="/library"
                  data-testid="success-open-library-bundle"
                  className="inline-flex items-center gap-3 bg-ink text-cream px-8 py-4 text-xs tracking-[0.25em] uppercase hover:bg-ink/80 transition-colors"
                >
                  <BookOpen className="w-4 h-4" strokeWidth={1.5}/>
                  Open My Library
                </Link>
              )}

              {/* Coaching: Calendly arrival message */}
              {isCoaching && (
                <div className="border border-ink/15 bg-white p-8 sm:p-10 text-left" data-testid="success-coaching">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-3">
                    Next steps
                  </p>
                  <h3 className="font-display text-3xl text-ink mb-3">
                    Your Calendly invitation arrives within 5 business days.
                  </h3>
                  <p className="text-ink/70">
                    The desk is reading your intake now. Expect a private booking link to schedule your 60-minute session — to be held within 30 days of this confirmation.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-14 max-w-2xl mx-auto">
                <Stat icon={BookOpen} label="In-browser library" sub="Read anytime"/>
                <Stat icon={Heart} label="10% donated" sub="to charity partners"/>
                <Stat icon={Sparkles} label="Lifetime access" sub="your shelf, your pace"/>
              </div>

              <div className="mt-16">
                <Link
                  to="/studio"
                  data-testid="success-back"
                  className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-ink/70 hover:text-ink"
                >
                  Back to the Studio
                  <ArrowUpRight className="w-3.5 h-3.5"/>
                </Link>
              </div>
            </motion.div>
          ) : status.status === "expired" ? (
            <>
              <div className="w-16 h-16 mx-auto bg-ink/10 grid place-items-center rounded-full mb-6">
                <X className="w-7 h-7 text-ink/60" strokeWidth={1.4}/>
              </div>
              <h1 className="font-display text-4xl mb-4">Session expired</h1>
              <p className="text-ink/70 mb-8">The checkout window timed out before payment cleared.</p>
              <Link to={`/studio/${status.slug}`} className="inline-flex items-center gap-2 bg-ink text-cream px-7 py-3 text-xs tracking-[0.25em] uppercase">
                Try again
              </Link>
            </>
          ) : (
            <OrderInProgress phase="finalizing" />
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, sub }) {
  return (
    <div className="border border-ink/15 bg-white p-5">
      <Icon className="w-4 h-4 text-ink mx-auto mb-3" strokeWidth={1.5}/>
      <p className="text-[10px] tracking-[0.25em] uppercase text-ink mb-1">{label}</p>
      <p className="text-[11px] text-ink/50 truncate">{sub}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// EDITORIAL "ORDER IN PROGRESS" LOADER
// Orbits an M Media star around the M-monogram while cycling through
// editorial status copy. Reassures buyers it can take up to ~2 minutes
// without scaring them. Tasteful, on-brand, fun to watch.
// ─────────────────────────────────────────────────────────────────────
const STATUS_LINES = [
  { kicker: "Verifying", body: "Confirming your card with Stripe…" },
  { kicker: "Issuing",   body: "Minting your edition certificate…" },
  { kicker: "Binding",   body: "Stitching the spine, dressing the cover…" },
  { kicker: "Couriering", body: "Walking it to your private library shelf…" },
  { kicker: "Engraving", body: "Setting your name into the colophon…" },
  { kicker: "Sealing",   body: "Slipping the receipt into a wax-sealed envelope…" },
  { kicker: "Almost there", body: "Polishing the brass, then it's all yours…" },
];

function OrderInProgress({ phase = "confirming" }) {
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % STATUS_LINES.length), 2400);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const line = STATUS_LINES[step];
  const headline = phase === "confirming" ? "Confirming your order" : "Finalizing your order";

  return (
    <div className="text-center max-w-xl mx-auto" data-testid="order-in-progress">
      {/* Orbiting star around the M-monogram */}
      <div className="relative w-40 h-40 sm:w-44 sm:h-44 mx-auto mb-10">
        {/* Outer orbit ring */}
        <motion.div
          className="absolute inset-0 rounded-full border border-ink/15"
          animate={{ rotate: 360 }}
          transition={{ duration: 14, ease: "linear", repeat: Infinity }}
        >
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-ink rotate-45"/>
        </motion.div>
        {/* Inner orbit ring (counter-rotating) */}
        <motion.div
          className="absolute inset-3 rounded-full border border-ink/10"
          animate={{ rotate: -360 }}
          transition={{ duration: 9, ease: "linear", repeat: Infinity }}
        >
          <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-ink/70"/>
        </motion.div>
        {/* M monogram in the centre */}
        <div className="absolute inset-0 grid place-items-center">
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            className="font-display text-5xl text-ink leading-none"
          >
            M
            <span className="block w-6 h-px bg-ink/40 mx-auto mt-1"/>
            <span className="block text-[8px] tracking-[0.4em] text-ink/50 mt-1">MEDIA</span>
          </motion.div>
        </div>
        {/* Single pulse dot orbiting on a 3rd ring */}
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 5.5, ease: "linear", repeat: Infinity }}
        >
          <motion.div
            className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 rounded-full bg-ink"
            animate={{ scale: [1, 1.6, 1], opacity: [1, 0.6, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
        </motion.div>
      </div>

      <p className="text-[10px] tracking-[0.4em] uppercase text-ink/55 mb-3">{headline}</p>

      {/* Rotating editorial status — cross-fades between lines */}
      <div className="min-h-[88px] sm:min-h-[100px] flex flex-col items-center justify-center">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink/60 mb-2">{line.kicker}</p>
          <p className="font-serif italic text-xl sm:text-2xl text-ink/85 leading-snug max-w-md">
            {line.body}
          </p>
        </motion.div>
      </div>

      {/* Reassurance banner */}
      <div className="mt-10 border-y border-ink/10 py-5 max-w-md mx-auto">
        <p className="text-[10px] tracking-[0.28em] uppercase text-ink/70 mb-2">Hang tight · please don&apos;t close this page</p>
        <p className="text-sm text-ink/65 leading-relaxed">
          This can take up to <span className="font-semibold text-ink">two minutes</span> while Stripe finishes processing your payment and we hand-deliver your edition to your shelf. <span className="font-serif italic">It&apos;s worth the wait.</span>
        </p>
      </div>

      <p className="text-[10px] tracking-[0.3em] uppercase text-ink/40 mt-6">
        Elapsed · {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// SINGLE-EDITION POST-PURCHASE UPSELL
// "Add 4 more to your shelf for $110 — save $36+"
// Shows the bundle math, primary CTA → studio in bundle-mode w/ slug
// pre-selected, secondary CTA → open the edition they just bought.
// ────────────────────────────────────────────────────────────────
function SingleProductUpsell({ status }) {
  const amountPaid = Number(status?.amount || 0);
  const BUNDLE_PRICE = 129;
  const SINGLE_ITEM_EST = 33; // average paid edition price across the catalog
  // Remaining cost to fill the bundle (rounded to nearest dollar, floor 0)
  const remaining = Math.max(BUNDLE_PRICE - amountPaid, 0);
  // Conservative savings estimate: 4 more editions at avg $33 = $132; bundle adds $129 - paid
  // i.e. the buyer pays $remaining for 4 editions worth ~$132 → savings ≈ $132 - remaining.
  const savings = Math.max(Math.round(SINGLE_ITEM_EST * 4 - remaining), 36);
  const remainingLabel = remaining === 0 ? "free" : `$${remaining}`;

  return (
    <div data-testid="success-upsell-card" className="text-left max-w-xl mx-auto">
      <div className="relative bg-white border border-ink/15 p-7 sm:p-9 overflow-hidden">
        {/* corner ribbon */}
        <div className="absolute -top-3 -right-3 bg-ink text-cream px-4 py-1.5 text-[10px] tracking-[0.28em] uppercase rotate-3 shadow-md">
          The Desk&apos;s Offer
        </div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-ink/55 mb-2">
          Make this a Pick-5 bundle
        </p>
        <h3 className="font-display text-3xl sm:text-4xl leading-[1.05] mb-4">
          Add 4 more editions
          <br/>
          <span className="font-serif italic font-light text-ink/80">
            for just {remainingLabel} more.
          </span>
        </h3>
        <p className="text-ink/65 mb-5 text-sm leading-relaxed">
          Your bundle is already <span className="font-semibold text-ink">{Math.round((amountPaid / BUNDLE_PRICE) * 100)}% paid</span>.
          Top it off with 4 more editions of your choice and you walk away with a
          full <span className="font-serif italic">five-edition shelf</span> for <span className="font-semibold text-ink">$129 flat</span> —
          saving you ~<span className="font-semibold text-ink">${savings}</span> vs buying them separately.
        </p>
        <div className="flex items-center gap-6 mb-6 border-t border-b border-ink/10 py-4">
          <div>
            <p className="text-[9px] tracking-[0.28em] uppercase text-ink/50 mb-1">Already paid</p>
            <p className="font-display text-xl">${amountPaid}</p>
          </div>
          <div className="w-px h-10 bg-ink/15"/>
          <div>
            <p className="text-[9px] tracking-[0.28em] uppercase text-ink/50 mb-1">Add 4 more for</p>
            <p className="font-display text-xl text-ink">{remainingLabel}</p>
          </div>
          <div className="w-px h-10 bg-ink/15"/>
          <div>
            <p className="text-[9px] tracking-[0.28em] uppercase text-ink/50 mb-1">Total bundle</p>
            <p className="font-display text-xl">${BUNDLE_PRICE}</p>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <Link
            to={`/studio?upsell_slug=${encodeURIComponent(status.slug)}`}
            data-testid="success-upsell-yes"
            className="group inline-flex items-center justify-center gap-3 bg-ink text-cream px-7 py-4 text-xs tracking-[0.25em] uppercase hover:bg-ink/85 transition-colors"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5}/>
            Add 4 more → Save ${savings}+
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"/>
          </Link>
          <Link
            to={`/library/${status.slug}`}
            data-testid="success-upsell-skip"
            className="inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.25em] uppercase text-ink/60 hover:text-ink py-2"
          >
            <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5}/>
            No thanks — just open my edition
          </Link>
        </div>
        <p className="text-[10px] text-ink/45 italic text-center mt-4 leading-relaxed">
          Pick any 4 of 40+ editions · same instant in-browser access · all sales final.
        </p>
      </div>
    </div>
  );
}
